// eslint-disable-next-line import/no-cycle
import {
  Property, StringMap, TemplateParentTypeEnum, TemplateStructureEnum,
} from '@/types';
// eslint-disable-next-line import/no-cycle
import {
  getSectionIdsFromIframe, setActiveSection,
  TemplateEditorState as state, updateSectionsInState,
} from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import { asInt } from '@/helpers';
// eslint-disable-next-line import/no-cycle
import createTimedLRUCache from '@/helpers/createTimedLRUCache';
// eslint-disable-next-line import/no-cycle
import { useEditorBackground } from '@/composables/useEditorBackground';
// eslint-disable-next-line import/no-cycle
import { invalidateCssPropertyCacheForSelector } from '../getters';
// eslint-disable-next-line import/no-cycle
import {
  TEMPLATE_SECTION_IDENTIFIER,
  TEMPLATE_LINE_IDENTIFIER,
  TEMPLATE_COLUMN_IDENTIFIER,
  TEMPLATE_WIDGET_IDENTIFIER,
  TEMPLATE_STRUCTURE_MAP,
  TEMPLATE_ITEM_IDENTIFIER_PREFIX_MAP,
} from './constants';
// eslint-disable-next-line import/no-cycle
import {
  createNewStylesheet,
  generateUniqStructureId,
  getSectionParent,
  getTemplateIframeDocument, isDisplayTemplate, isEmailTemplate, replaceSelectors,
} from './helpers';

const GLOBAL_CSS = 'spm_styles_to_keep';

/**
 * Check if a mobile stylesheet should be skipped (mobile styles are disabled)
 * @param stylesheet - The stylesheet element to check
 * @returns true if the stylesheet should be skipped
 */
const shouldSkipMobileStylesheet = (stylesheet: HTMLStyleElement): boolean => {
  const stylesType = stylesheet.getAttribute('data-spm-styles') ?? '';
  const isMobileStylesheet = /_mobile$/.test(stylesType);
  const hasMobileDisabledComment = stylesheet.innerHTML.includes('/*data-spmhidemobile');
  return isMobileStylesheet && hasMobileDisabledComment;
};

export const getTemplateSections = (): HTMLElement[] => {
  const template = getTemplateIframeDocument();
  return Array.from(template?.querySelector('#spm_body:not(.spm_facebook)')?.querySelectorAll(TEMPLATE_SECTION_IDENTIFIER) || []);
};

export const getTemplateLines = (): HTMLElement[] => {
  const template = getTemplateIframeDocument();
  return Array.from(template?.querySelectorAll(TEMPLATE_LINE_IDENTIFIER) || []);
};

export const getTemplateColumns = (): HTMLElement[] => {
  const template = getTemplateIframeDocument();
  return Array.from(template?.querySelectorAll(TEMPLATE_COLUMN_IDENTIFIER) || []);
};

export const getTemplateWidgets = (templateIframe: Document|null = null): HTMLElement[] => {
  const template = templateIframe || getTemplateIframeDocument();
  return Array.from(template?.querySelectorAll(TEMPLATE_WIDGET_IDENTIFIER) || []);
};

export const getTemplateForms = (templateIframe: Document|null = null): HTMLElement[] => {
  const template = templateIframe || getTemplateIframeDocument();
  return Array.from(template?.querySelectorAll(`${TEMPLATE_WIDGET_IDENTIFIER}.spm_widget_form`) || []);
};

export const getTemplateFormButtons = (templateIframe: Document|null = null): HTMLElement[] => {
  const template = templateIframe || getTemplateIframeDocument();
  const sendFormElements = Array.from(template?.querySelectorAll('[data-send-form]') || []);
  const widgets: HTMLElement[] = [];
  sendFormElements.forEach((el) => {
    const widget = el.closest<HTMLElement>(TEMPLATE_WIDGET_IDENTIFIER);
    if (widget && !widgets.includes(widget)) {
      widgets.push(widget);
    }
  });
  return widgets;
};

export const getGlobalCSS = (template: HTMLDocument): CSSStyleSheet | undefined => {
  const { styleSheets } = template;
  return Object.values(styleSheets).find(({ title }: CSSStyleSheet) => title === GLOBAL_CSS);
};

export const getDynamicStylesForActiveSection = (
  sectionId: string | number | null = null,
  mobile = false,
  forceStylesSheet: string | false = false,
): HTMLStyleElement => {
  let styleType: string;

  if (forceStylesSheet) {
    styleType = forceStylesSheet;
  } else if (mobile === true) {
    styleType = 'dynamic_persistent_mobile';
  } else {
    styleType = 'dynamic';
  }

  const activeSection = sectionId ?? state.template?.activeSection;

  return getTemplateIframeDocument().querySelector(
    `[data-spm-styles="${styleType}"][data-spm-section="${activeSection}"]`,
  ) as HTMLStyleElement;
};


/**
 * Resolve the correct section ID from a CSS selector by finding the element in the DOM
 * and walking up to the parent section. Falls back to design section for global selectors.
 */
const resolveSectionIdFromSelector = (selector: string): string | number | null => {
  const template = getTemplateIframeDocument();
  if (!template) return null;

  // For global selectors (not a specific widget/section/row/column), use design section
  const idWidget = selector.match(new RegExp('#spm_(?:widget|section|row|column)_([a-zA-Z0-9]+)'));
  const isWidget = Array.isArray(idWidget) && idWidget.length > 0;
  if (!isWidget && state.template?.content?.design) {
    return state.template.content.design;
  }

  const element = template.querySelector(selector);
  if (element) {
    const section = element.closest('[data-spmelementid]');
    if (section) {
      return section.getAttribute('data-spmelementid');
    }
  }

  return null;
};

export const addNewDynamicStyle = (selector: string, value: StringMap, mobile = false, forceStylesSheet: string | false = false) => {
  const resolvedSectionId = resolveSectionIdFromSelector(selector) ?? state.template?.activeSection;
  if (resolvedSectionId && resolvedSectionId !== -1) {
    let stylesheet = getDynamicStylesForActiveSection(resolvedSectionId, mobile, forceStylesSheet);
    if (!stylesheet) {
      let styleType: string;
      if (forceStylesSheet) {
        styleType = forceStylesSheet;
      } else if (mobile === true) {
        styleType = 'dynamic_persistent_mobile';
      } else {
        styleType = 'dynamic';
      }
      // Section has no dynamic styles stylesheet, we create it
      stylesheet = createNewStylesheet(styleType, resolvedSectionId.toString());
      if (mobile === true && stylesheet) {
        stylesheet.innerHTML = '@media only screen and (max-width: 600px) {}';
      }
    }

    if (stylesheet) {
      // Skip entire processing if mobile styles are disabled
      if (shouldSkipMobileStylesheet(stylesheet)) {
        return;
      }

      const { sheet } = stylesheet;
      if (sheet) {
        Object.entries(value)
          .forEach(([property, propertyValue]) => {
            if (propertyValue && propertyValue !== '') {
              if (mobile !== true) {
                sheet.insertRule(`${selector} { ${property}: ${propertyValue}; }`, sheet.cssRules.length);
              } else {
                Array.from(sheet.cssRules).forEach((rule: any) => {
                  if (rule.constructor.name === 'CSSMediaRule') {
                    const mediaRule = rule as CSSMediaRule;
                    mediaRule.insertRule(`${selector} { ${property}: ${propertyValue}; }`, mediaRule.cssRules.length);
                  }
                });
              }
            }
          });
        stylesheet.innerHTML = Array.from(sheet.cssRules)
          .map((rule) => rule.cssText)
          .join('\n');
      }
    }
  }
};

export const moveDynamicStyleFromSectionToAnother = (selector: string, sectionIdFrom: string, sectionIdTo: string) => {
  const template = getTemplateIframeDocument();
  if (sectionIdFrom && sectionIdTo) {
    const dynamicStylesFrom = getDynamicStylesForActiveSection(sectionIdFrom);
    const dynamicStylesTo = getDynamicStylesForActiveSection(sectionIdTo);
    if (dynamicStylesFrom && dynamicStylesTo) {
      const { sheet: sheetFrom } = dynamicStylesFrom;
      const { sheet: sheetTo } = dynamicStylesTo;

      if (sheetFrom && sheetTo) {
        let reconstructFrom = false;
        let index = 0;

        const rulesToAdd: string[] = [];

        Object.values(sheetFrom.cssRules).forEach((styleRule: any) => {
          if (typeof styleRule.selectorText === 'string' && styleRule.selectorText.match(selector)) {
            rulesToAdd.push(styleRule.cssText);
            sheetFrom.deleteRule(index);
            index -= 1;
            reconstructFrom = true;
          }
          index += 1;
        });

        // Reconstruct the innerHTML of the source section's dynamic styles
        if (reconstructFrom) {
          dynamicStylesFrom.innerHTML = Array.from(sheetFrom.cssRules)
            .map((rule) => rule.cssText)
            .join('\n');
        }

        // Add the collected rules to the target section's dynamic styles
        if (rulesToAdd.length > 0) {
          rulesToAdd.forEach((rule) => {
            sheetTo.insertRule(rule, sheetTo.cssRules.length);
          });

          // Reconstruct the innerHTML of the target section's dynamic styles
          dynamicStylesTo.innerHTML = Array.from(sheetTo.cssRules)
            .map((rule) => rule.cssText)
            .join('\n');
        }
      }
    }
  }
};

export const removeDynamicStyle = (selector: string, value: StringMap, mobile = false, forceStylesSheet: string | false = false) => {
  // We check if selector is dedicated to a widget, section, row or column
  const idWidget = selector.match(new RegExp('#spm_(?:widget|section|row|column)_([a-zA-Z0-9]+)'));
  const isWidget = Array.isArray(idWidget) && idWidget.length > 0;
  const template = getTemplateIframeDocument();

  // Remove inline styles for the targeted properties (inline styles have higher specificity)
  if (template && !mobile) {
    const elements = template.querySelectorAll(selector);
    elements.forEach((element) => {
      const htmlElement = element as HTMLElement;
      if (htmlElement.style) {
        Object.keys(value).forEach((property) => {
          if (htmlElement.style.getPropertyValue(property)) {
            htmlElement.style.removeProperty(property);
          }
        });
      }
    });
  }

  let needApplyBackgroundReferenceFromSelector = false;
  getSectionIdsFromIframe(!isWidget).forEach((sectionId) => {
    // eslint-disable-next-line radix
    if (sectionId) {
      const dynamicStyles = getDynamicStylesForActiveSection(sectionId, mobile, forceStylesSheet);
      if (dynamicStyles) {
        // Skip entire processing if mobile styles are disabled
        if (shouldSkipMobileStylesheet(dynamicStyles)) {
          return;
        }

        const { sheet } = dynamicStyles;
        if (sheet) {
          let reconstruct = false;
          const reconstructPadding: any[] = [];
          Object.keys(value).forEach((property) => {
            if (['background', 'background-color'].includes(property)) {
              needApplyBackgroundReferenceFromSelector = true;
            }
            const cssRuleSelector = property.replace(/-./g, (char) => char.toUpperCase()[1]);
            let index = 0;

            // Extraire toutes les classes .spm_xxx d'un sélecteur
            const extractSpmClasses = (sel: string): string[] => {
              const matches = sel.match(/\.spm_[a-zA-Z0-9_]+/g);
              return matches ? [...matches].sort() : [];
            };

            const processCssRule = (rule: CSSRule, mediaSheet?: CSSMediaRule) => {
              const styleRule = rule as CSSStyleRule;
              const currentSheet = mediaSheet || sheet;
              if (typeof styleRule.selectorText === 'string') {
                let notWidgetCondition = !isWidget && styleRule.selectorText === selector;
                if (cssRuleSelector === 'backgroundImage') {
                  // Check comma-separated selector parts match exactly (not substring)
                  // background-image is applied with multiple selectors (e.g. "#spm_body.spm_popin #spm_content, #spm_body #spm_content")
                  const inputParts = selector.split(',').map(s => s.trim());
                  const ruleParts = styleRule.selectorText.split(',').map(s => s.trim());
                  const allInputPartsMatch = inputParts.every(p => ruleParts.includes(p));
                  notWidgetCondition = !isWidget && allInputPartsMatch;
                }
                const currentIdWidget = styleRule.selectorText.match(new RegExp('#spm_(?:widget|section|row|column)_([a-zA-Z0-9]+)'));
                const currentIsWidget = Array.isArray(currentIdWidget) && currentIdWidget.length > 0;

                // Vérifier que les classes .spm_xxx sont identiques entre les deux sélecteurs
                const selectorSpmClasses = extractSpmClasses(selector);
                const ruleSpmClasses = extractSpmClasses(styleRule.selectorText);
                const sameSpmClasses = JSON.stringify(selectorSpmClasses) === JSON.stringify(ruleSpmClasses);

                // Extract the full element ID (including the _content suffix) from both selectors
                // to avoid removing rules that target the same DOM element via a different selector path
                const fullIdRegex = /#(spm_(?:widget|section|row|column)_[a-zA-Z0-9]+(?:_content)?)/;
                const selectorFullId = selector.match(fullIdRegex);
                const ruleFullId = styleRule.selectorText.match(fullIdRegex);
                const sameFullElementId = selectorFullId && ruleFullId && selectorFullId[1] === ruleFullId[1];

                const selectorMatches = (notWidgetCondition || (!isWidget && !currentIsWidget && sameSpmClasses && template.querySelector(selector) && template.querySelector(selector) === template.querySelector(styleRule.selectorText)))
                    || (idWidget && isWidget && (styleRule.selectorText === selector
                      || (currentIdWidget && currentIsWidget && sameFullElementId && template.querySelector(selector) === template.querySelector(styleRule.selectorText))));
                // CSS custom properties (--*) can't be accessed via bracket notation on CSSStyleDeclaration
                const styleHasProperty = property.startsWith('--')
                  ? !!styleRule.style.getPropertyValue(property)
                  : (styleRule.style[cssRuleSelector as any] || (cssRuleSelector.match('padding') && styleRule.style['padding' as any]));
                if (
                  selectorMatches
                  && styleHasProperty
                ) {

                  if (cssRuleSelector === 'backgroundImage' && (styleRule.style[cssRuleSelector].match('gradient') || styleRule.style.backgroundColor.match('rgb'))) {
                    let bgColor = styleRule.style.backgroundColor;
                    if (styleRule.style[cssRuleSelector].match('gradient')) {
                      bgColor = styleRule.style[cssRuleSelector].replace(/.*((linear|radial)-gradient.*\))$/, '$1');
                    }
                    currentSheet.insertRule(`${selector} {background: ${bgColor};}`, index);
                    index += 1;
                    currentSheet.deleteRule(index);
                    index -= 1;
                    reconstruct = true;
                  } else if (styleRule.cssText.match(`${property}:`) || (styleRule.cssText.match('padding:') && cssRuleSelector.match('padding'))) {
                    currentSheet.deleteRule(index);
                    index -= 1;
                    reconstruct = true;
                    if (styleRule.cssText.match('padding:')) {
                      const paddings = ['padding-top', 'padding-right', 'padding-bottom', 'padding-left'];
                      paddings.forEach((padding: any) => {
                        if (padding !== property && styleRule.style[padding]) {
                          const paddingStyle = { [padding]: styleRule.style[padding] };
                          reconstructPadding.push(paddingStyle);
                        }
                      });
                    }
                  }
                }
              }
            };

            Array.from(sheet.cssRules).forEach((styleRule) => {
              if (styleRule.constructor.name === 'CSSStyleRule') {
                processCssRule(styleRule as CSSStyleRule);
                index += 1;
              } else if (styleRule.constructor.name === 'CSSMediaRule') {
                index = 0;
                Array.from((styleRule as CSSMediaRule).cssRules).forEach((mediaRule: CSSRule) => {
                  processCssRule(mediaRule as CSSStyleRule, styleRule as CSSMediaRule);
                  index += 1;
                });
              }
            });
          });
          if (reconstruct) {
            dynamicStyles.innerHTML = Array.from(sheet.cssRules)
              .map((rule) => rule.cssText)
              .join('\n');

            if (reconstructPadding.length) {
              reconstructPadding.forEach((padding) => {
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                addOrUpdateDynamicStyle(selector, padding, mobile);
              });
            }
          }
        }
      }
    }
  });
  if (needApplyBackgroundReferenceFromSelector) {
    const { applyBackgroundReferenceFromSelector } = useEditorBackground();
    applyBackgroundReferenceFromSelector(selector, template);
  }

  // NOTE: Line-height recalculation on font-size removal is disabled for now
  // to avoid conflicts with the main recalculateLineHeightValue method.
  // Will be re-enabled once the main method is fully tested.
};

export function extractOnlyMediaBlocks(css: string) {
  let result = '';
  let index = 0;

  while (index < css.length) {
    const mediaStart = css.indexOf('@media', index);
    if (mediaStart === -1) break;

    const openBrace = css.indexOf('{', mediaStart);
    if (openBrace === -1) break;

    let depth = 1;
    let cursor = openBrace + 1;

    while (cursor < css.length && depth > 0) {
      const char = css[cursor];
      if (char === '{') depth += 1;
      else if (char === '}') depth -= 1;
      cursor += 1;
    }

    const block = css.slice(mediaStart, cursor).trim();
    result += block;

    index = cursor;
  }

  return result.trim();
}

function shouldExtractMediaBlocks(css: string) {
  const cleaned = css.replace(/@media[^{]+\{[^{}]*\{[^{}]*\}[^}]*\}/gs, '');
  return /[^{]+\{[^}]+\}/.test(cleaned);
}

export const cleanupDynamicStyle = () => {
  if (isDisplayTemplate(state.template?.type) || isEmailTemplate(state.template?.type)) {
    const template = getTemplateIframeDocument();
    let updateSections = false;
    const cssTextExists: any[] = [];

    /**
     * Clean orphaned rules from a stylesheet type: remove rules targeting elements
     * that don't exist in the DOM or belong to a different section.
     */
    const cleanupStylesheetType = (styleSheetType: string | false = false) => {
      getSectionIdsFromIframe(false).forEach((sectionId) => {
        // eslint-disable-next-line radix
        if (sectionId) {
          const dynamicStyles = getDynamicStylesForActiveSection(sectionId, false, styleSheetType);
          if (dynamicStyles) {
            // Skip entire processing if mobile styles are disabled
            if (shouldSkipMobileStylesheet(dynamicStyles)) {
              return;
            }

            const { sheet } = dynamicStyles;
            if (sheet) {
              let reconstruct = false;
              let index = 0;
              Object.values(sheet.cssRules).forEach((styleRule: any) => {
                if (typeof styleRule.selectorText === 'string') {
                  if (!cssTextExists.includes(styleRule.cssText)) {
                    cssTextExists.push(styleRule.cssText);
                    // We check if selector is dedicated to a widget or not
                    const idWidget = styleRule.selectorText.match(new RegExp('#spm_(?:widget|section|row|column)_([a-zA-Z0-9]+)'));
                    const isWidget = Array.isArray(idWidget) && idWidget.length > 0;
                    if (isWidget) {
                      const currentSectionId = template.querySelector(`[data-spmelementid="${sectionId}"]`) as HTMLElement;
                      // Remove pseudo-classes from selector before querySelector (querySelector doesn't support pseudo-classes)
                      const pseudoClasses = '(hover|focus|active|visited|link|before|after|first-child|last-child'
                        + '|nth-child\\([^)]+\\)|not\\([^)]+\\)|first-of-type|last-of-type|nth-of-type\\([^)]+\\)'
                        + '|only-child|only-of-type|empty|disabled|enabled|checked|valid|invalid)';
                      const selectorToCheck = styleRule.selectorText.replace(new RegExp(`::?${pseudoClasses}`, 'gi'), '');
                      const currentTarget = template.querySelector(selectorToCheck);
                      let deleteRule = false;
                      if (currentTarget) {
                        const targetSectionId = getSectionParent(currentTarget as HTMLElement);
                        if (currentSectionId
                            && targetSectionId
                            && currentSectionId.getAttribute('data-spmelementid') !== targetSectionId.getAttribute('data-spmelementid')
                        ) {
                          deleteRule = true;
                        }
                      } else {
                        deleteRule = true;
                      }
                      if (deleteRule) {
                        reconstruct = true;
                        sheet.deleteRule(index);
                        index -= 1;
                      }
                    }
                  } else {
                    reconstruct = true;
                    sheet.deleteRule(index);
                    index -= 1;
                  }
                }
                index += 1;
              });
              if (reconstruct) {
                dynamicStyles.innerHTML = Array.from(sheet.cssRules)
                  .map((rule) => rule.cssText)
                  .join('\n');
                updateSections = true;
              }
            }
          }
        }
      });
    };

    /**
     * Two sizes never come from a design choice and only do harm once sent:
     * - a width in fractional pixels is a computed layout value that leaked into the stylesheet through
     *   the width field; it pins the widget to its desktop size on every client and breaks mobile;
     * - a zero height on a section container shows nothing in the editor, but the sending pipeline
     *   copies it to a height attribute that Outlook applies literally, collapsing the section.
     * Drop both so the element follows its content again.
     */
    const stripStaleDimensions = (styleSheetType: string | false = false) => {
      getSectionIdsFromIframe(false).forEach((sectionId) => {
        if (!sectionId) {
          return;
        }
        const dynamicStyles = getDynamicStylesForActiveSection(sectionId, false, styleSheetType);
        const sheet = dynamicStyles?.sheet;
        if (!sheet || shouldSkipMobileStylesheet(dynamicStyles)) {
          return;
        }

        let reconstruct = false;
        const stripRule = (rule: CSSRule) => {
          const { style, selectorText } = rule as CSSStyleRule;
          if (!style) {
            return;
          }
          if (/^\d+\.\d+px$/.test(style.width)) {
            style.removeProperty('width');
            reconstruct = true;
          }
          if (/^0(px)?$/.test(style.height) && /\.container|#spm_section/.test(selectorText || '')) {
            style.removeProperty('height');
            reconstruct = true;
          }
        };
        Array.from(sheet.cssRules).forEach((rule) => {
          if (rule.constructor.name === 'CSSMediaRule') {
            Array.from((rule as CSSMediaRule).cssRules).forEach(stripRule);
          } else {
            stripRule(rule);
          }
        });

        if (reconstruct) {
          // eslint-disable-next-line no-param-reassign
          dynamicStyles.innerHTML = Array.from(sheet.cssRules)
            .filter((rule) => rule.constructor.name !== 'CSSStyleRule' || (rule as CSSStyleRule).style.length > 0)
            .map((rule) => rule.cssText)
            .join('\n');
          updateSections = true;
        }
      });
    };

    // Cleanup both dynamic and dynamic_persistent stylesheets
    cleanupStylesheetType();
    cleanupStylesheetType('dynamic_persistent');

    if (isEmailTemplate(state.template?.type)) {
      stripStaleDimensions();
      stripStaleDimensions('dynamic_persistent');
    }

    const stylesheets = template.querySelectorAll('style[data-spm-styles="dynamic_persistent_mobile"]');

    stylesheets.forEach((stylesheet) => {
      const original = stylesheet.innerHTML;

      // Skip if mobile styles are disabled (commented out)
      if (original.includes('/*data-spmhidemobile')) {
        return;
      }

      const hasMedia = /@media\s+[^{]+\{/.test(original);
      if (original && hasMedia && shouldExtractMediaBlocks(original)) {
        // eslint-disable-next-line no-param-reassign
        stylesheet.innerHTML = extractOnlyMediaBlocks(original);
      }
    });
    if (updateSections) {
      updateSectionsInState(false);
    }
  }
};

export const removeDynamicStyleWithoutPseudoClasses = (selector: string, property: Property) => {
  const { name, value } = property;
  let newSelector = selector;
  newSelector = newSelector.replace(new RegExp('(:[a-zA-Z-]+\\([-:.a-zA-Z#]+\\))+', 'g'), '');
  removeDynamicStyle(newSelector, { [name]: value });
};

/**
 * Specificity of one selector, weighted so that ids beat classes and classes beat elements.
 */
const getSelectorSpecificity = (selector: string): number => {
  const ids = (selector.match(/#[\w-]+/g) || []).length;
  const classes = (selector.match(/\.[\w-]+|\[[^\]]*\]|:(?!:)[\w-]+(?:\([^)]*\))?/g) || []).length;
  const elements = (selector.match(/(?:^|[\s>+~])[a-zA-Z][\w-]*|::[\w-]+/g) || []).length;
  return (ids * 100 + classes) * 100 + elements;
};

interface DeclaringRule {
  selectors: string[];
  style: CSSStyleDeclaration;
  order: number;
}

interface CascadedStyleCache {
  rules: WeakMap<Document, Map<string, DeclaringRule[]>>;
  values: WeakMap<HTMLElement, Map<string, string>>;
}

let cascadedStyleCache: CascadedStyleCache | null = null;

/**
 * Runs a pass that reads the cascade many times without changing it, so that each value is resolved
 * once: nothing that selectors or the read properties depend on may change during the pass.
 */
export const withCascadedStyleCache = <T>(pass: () => T): T => {
  if (cascadedStyleCache) return pass();
  cascadedStyleCache = { rules: new WeakMap(), values: new WeakMap() };
  try {
    return pass();
  } finally {
    cascadedStyleCache = null;
  }
};

interface KeyedStore<K, V> {
  has(key: K): boolean;
  get(key: K): V | undefined;
  set(key: K, value: V): unknown;
}

const getOrCompute = <K, V>(store: KeyedStore<K, V>, key: K, compute: () => V): V => {
  if (!store.has(key)) {
    store.set(key, compute());
  }
  return store.get(key) as V;
};

/**
 * Rules of the desktop sheets of every section that declare a property, with their source order.
 */
const getDeclaringRules = (ownerDocument: Document, property: string): DeclaringRule[] => {
  const rules: DeclaringRule[] = [];
  let order = 0;

  Array.from(ownerDocument.styleSheets).forEach((sheet) => {
    const sheetType = (sheet.ownerNode as HTMLElement | null)?.dataset?.spmStyles;
    if (!sheetType || /_mobile$/.test(sheetType)) return;

    Array.from(sheet.cssRules).forEach((rule) => {
      const { selectorText, style } = rule as CSSStyleRule;
      order += 1;
      if (typeof selectorText !== 'string' || !style || !style.getPropertyValue(property)) return;
      rules.push({ selectors: selectorText.split(','), style, order });
    });
  });

  return rules;
};

const resolveCascadedStyleValue = (element: HTMLElement, property: string, rules: DeclaringRule[]): string => {
  let value = element.style.getPropertyValue(property);
  let rank = -1;
  if (value) {
    rank = element.style.getPropertyPriority(property) === 'important' ? 3e12 : 1e12;
  }

  rules.forEach(({ selectors, style, order }) => {
    const matching = selectors.filter((part) => {
      try {
        return element.matches(part.trim());
      } catch (error) {
        return false;
      }
    });
    if (!matching.length) return;

    const specificity = Math.max(...matching.map(getSelectorSpecificity));
    const ruleRank = (style.getPropertyPriority(property) === 'important' ? 2e12 : 0) + specificity * 1e6 + order;
    if (ruleRank > rank) {
      rank = ruleRank;
      value = style.getPropertyValue(property);
    }
  });

  return value.trim();
};

/**
 * Value a property resolves to for an element once every template stylesheet is applied, the way the
 * sending pipeline inlines them: inline style, then the desktop sheets of every section, by importance,
 * specificity and source order. Mobile sheets and media queries are ignored. Empty when nothing
 * declares the property.
 */
export const getCascadedStyleValue = (element: HTMLElement, property: string): string => {
  const { ownerDocument } = element;
  if (!cascadedStyleCache) {
    return resolveCascadedStyleValue(element, property, getDeclaringRules(ownerDocument, property));
  }
  const rulesByProperty = getOrCompute(cascadedStyleCache.rules, ownerDocument, () => new Map<string, DeclaringRule[]>());
  const values = getOrCompute(cascadedStyleCache.values, element, () => new Map<string, string>());
  return getOrCompute(values, property, () => resolveCascadedStyleValue(
    element,
    property,
    getOrCompute(rulesByProperty, property, () => getDeclaringRules(ownerDocument, property)),
  ));
};

const dynamicStylesCache = createTimedLRUCache<string, StringMap>({
  timeout: 500,
  maxSize: 200,
});

export const clearDynamicStylesCache = () => {
  dynamicStylesCache.clear();
};

export const getDynamicStylesForSelector = (selector: string, mobile = false):
  StringMap => dynamicStylesCache.getOrCompute(`${selector}|${mobile === true ? 'mobile' : 'desktop'}`, () => {
  const selectorStyles: StringMap = {};
  const idWidget = selector.match(new RegExp('#spm_(?:widget|section|row|column)_([a-zA-Z0-9]+)'));
  const isWidget = Array.isArray(idWidget) && idWidget.length > 0;

  const template = getTemplateIframeDocument();

  // Check if selector contains pseudo-classes
  const pseudoClassPattern = '(hover|focus|active|visited|link|before|after|first-child|last-child'
    + '|nth-child\\([^)]+\\)|not\\([^)]+\\)|first-of-type|last-of-type|nth-of-type\\([^)]+\\)'
    + '|only-child|only-of-type|empty|disabled|enabled|checked|valid|invalid)';
  const pseudoClassRegex = new RegExp(`::?${pseudoClassPattern}`, 'i');
  const hasPseudoClass = pseudoClassRegex.test(selector);

  // If selector has pseudo-class, remove it for querySelector
  const pseudoClasses = pseudoClassPattern;
  const selectorWithoutPseudo = hasPseudoClass
    ? selector.replace(new RegExp(`::?${pseudoClasses}`, 'gi'), '')
    : selector;
  const targetElement = template.querySelector(selectorWithoutPseudo);

  const sectionId = resolveSectionIdFromSelector(selectorWithoutPseudo) ?? state.template?.activeSection;
  if (!sectionId || sectionId === -1) {
    return selectorStyles;
  }

  const dynamicStylesheet = getDynamicStylesForActiveSection(sectionId, mobile);
  const { sheet } = dynamicStylesheet || {};
  if (!sheet) {
    return selectorStyles;
  }

  const createSelectorStyles = (rule: CSSStyleRule) => {
    const { selectorText, style } = rule as CSSStyleRule;
    if (!selectorText) return;

    const currentIdWidgetMatch = selectorText.match(/#spm_(?:widget|section|row|column)_([a-zA-Z0-9]+)/);
    const currentIsWidget = Boolean(currentIdWidgetMatch);

    // For pseudo-classes, compare selectors textually instead of using querySelector
    const currentPseudoRegex = new RegExp(`::?${pseudoClassPattern}`, 'i');
    const currentHasPseudoClass = currentPseudoRegex.test(selectorText);
    const currentSelectorWithoutPseudo = currentHasPseudoClass
      ? selectorText.replace(new RegExp(`::?${pseudoClasses}`, 'gi'), '')
      : selectorText;
    const currentElement = template.querySelector(currentSelectorWithoutPseudo);

    // For exact match, compare full selectors (including pseudo-classes)
    const isExactMatch = selectorText === selector;

    // Extract pseudo-class from both selectors to compare them
    const extractPseudoClass = (sel: string) => {
      const pseudoPattern = '(hover|focus|active|visited|link|before|after|first-child|last-child'
        + '|nth-child\\([^)]+\\)|not\\([^)]+\\)|first-of-type|last-of-type|nth-of-type\\([^)]+\\)'
        + '|only-child|only-of-type|empty|disabled|enabled|checked|valid|invalid)';
      const match = sel.match(new RegExp(`::?${pseudoPattern}`, 'i'));
      return match ? match[0] : null;
    };

    // If requested selector has pseudo-class, match if same element AND same pseudo-class
    // Otherwise, match same element even if selectors differ slightly
    let shouldMatch = false;
    if (hasPseudoClass) {
      // Selector has :hover, :focus, etc.
      // Match if: exact selector OR (same element AND same pseudo-class)
      const requestedPseudoClass = extractPseudoClass(selector);
      const rulePseudoClass = extractPseudoClass(selectorText);
      const samePseudoClass = requestedPseudoClass === rulePseudoClass && requestedPseudoClass !== null;
      const isSameElement = !isWidget && currentElement === targetElement && targetElement !== null;
      const isWidgetMatch = isWidget && currentIsWidget && currentElement === targetElement && targetElement !== null;
      shouldMatch = isExactMatch || (samePseudoClass && (isSameElement || isWidgetMatch));
    } else {
      // Normal selector -> match by element or exact selector
      const isSameElement = !isWidget && currentElement === targetElement && targetElement !== null;
      const isWidgetMatch = isWidget && (selectorText === selector || (currentIsWidget && currentElement === targetElement && targetElement !== null));
      shouldMatch = isExactMatch || isSameElement || isWidgetMatch;
    }

    if (shouldMatch) {
      Array.from({ length: style.length }).forEach((_, i) => {
        const cssProp = style[i];
        const value = style.getPropertyValue(cssProp);
        if (value) {
          selectorStyles[cssProp] = value;
        }
      });
    }
  };

  Array.from(sheet.cssRules)
    .forEach((rule) => {
      if (rule.constructor.name === 'CSSStyleRule') {
        createSelectorStyles(rule as CSSStyleRule);
      } else if (rule.constructor.name === 'CSSMediaRule') {
        Array.from((rule as CSSMediaRule).cssRules).forEach((mediaRule: CSSRule) => {
          if (mediaRule.constructor.name === 'CSSStyleRule') {
            createSelectorStyles(mediaRule as CSSStyleRule);
          }
        });
      }
    });
  return selectorStyles;
});

export const addOrUpdateDynamicStyle = (selector: string, value: StringMap, mobile = false, forceStylesSheet: string | false = false) => {
  // Get current styles to compare and detect changes
  const currentStyles = getDynamicStylesForSelector(selector, mobile);

  // Detect which properties are actually changing
  const changedProperties: string[] = [];
  Object.keys(value).forEach((prop) => {
    if (currentStyles[prop] !== value[prop]) {
      changedProperties.push(prop);
    }
  });

  removeDynamicStyle(selector, value, mobile, forceStylesSheet);
  addNewDynamicStyle(selector, value, mobile, forceStylesSheet);

  // Invalidate cache only for properties that actually changed
  if (changedProperties.length > 0) {
    changedProperties.forEach((prop) => {
      invalidateCssPropertyCacheForSelector(selector, prop, mobile);
    });
    // Also clear dynamicStylesCache for this selector since styles changed
    clearDynamicStylesCache();
  }
};

export const getChildrenType = (parentType: TemplateStructureEnum, templateType: TemplateParentTypeEnum): TemplateStructureEnum => {
  switch (parentType) {
    case TemplateStructureEnum.PAGE: return templateType === TemplateParentTypeEnum.EMAIL ? TemplateStructureEnum.SECTION : TemplateStructureEnum.LINE;
    case TemplateStructureEnum.SECTION: return TemplateStructureEnum.LINE;
    case TemplateStructureEnum.LINE: return TemplateStructureEnum.COLUMN;
    default: return TemplateStructureEnum.WIDGET;
  }
};

export const getChildrenStructure = (parentId: string, childrenType: string): Array<HTMLElement> => {
  let childrenStructures: Array<HTMLElement> = [];
  const parentIdentifier = parentId.indexOf('#') !== -1 ? parentId : `#${parentId}`;
  const template = getTemplateIframeDocument();
  const parentStructure = template.querySelector(parentIdentifier);
  if (parentStructure) {
    childrenStructures = Array.from(parentStructure.querySelectorAll(TEMPLATE_STRUCTURE_MAP[childrenType]));
  }
  return childrenStructures;
};

export const getParentClosestStructure = (elementId: string, structure: string): string|null => {
  const template = getTemplateIframeDocument();
  const element = template?.querySelector(`#${elementId}`) as HTMLElement;
  if (element) {
    const parent = (element.closest(TEMPLATE_STRUCTURE_MAP[structure]) as HTMLElement);
    if (parent) {
      if (!parent.id) { // Generate id if not exists
        const parentId = `${TEMPLATE_ITEM_IDENTIFIER_PREFIX_MAP[structure]}_${generateUniqStructureId()}`;
        parent.setAttribute('id', parentId);
      }
      return parent.id;
    }
  }
  return null;
};

export const addValuesToProperties = (
  selector: string,
  properties: Array<Property>,
  fieldGroupSelector: string,
  structureId: string,
  parentColumnId: string,
  parentLineId: string,
  mobile = false,
): Array<Property> => properties.map((property: Property) => {
  const rawSelector = property.customSelector ?? selector;

  let resolvedSelector = replaceSelectors(rawSelector, '{SELECTOR}', fieldGroupSelector);
  resolvedSelector = replaceSelectors(resolvedSelector, '{ID}', structureId);
  resolvedSelector = replaceSelectors(resolvedSelector, '{COLUMN_ID}', parentColumnId);
  resolvedSelector = replaceSelectors(resolvedSelector, '{LINE_ID}', parentLineId);

  if (property.customSelector) {
    // eslint-disable-next-line no-param-reassign
    property.customSelector = resolvedSelector;
  }
  // eslint-disable-next-line no-param-reassign
  property.value = mobile
    ? property.getter(resolvedSelector, property.name, structureId, true)
    : property.getter(resolvedSelector, property.name, structureId);
  return property;
});

export const computeActiveSection = (type: string, identifier: string): string | number => {
  if (state.template) {
    if (state.template.type !== 'email') return state.template.content.sections[0]; // template is not email => only one section
    if (type === TemplateStructureEnum.PAGE) return state.template.content.design; // click on global style button => design section

    const template = getTemplateIframeDocument();
    const structureId = getParentClosestStructure(identifier, 'section'); // get the parent section identifier
    if (structureId) {
      const elementId = (template?.querySelector(`#${structureId}`) as HTMLElement).getAttribute('data-spmelementid'); // get the section id
      if (elementId) {
        return asInt(elementId);
      }
    }
  }
  return -1;
};
